import { useState } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';

type MakeupStatus = "pending" | "scheduled" | "completed" | "waived";

// ════════════════════════════════════════════════════════════
// COACH HOOKS
// ════════════════════════════════════════════════════════════

export function useCoachMakeups(statusFilter?: MakeupStatus) {
  const data = useQuery(api.coach.makeups.list, { statusFilter });
  return { data, isLoading: data === undefined };
}

export function useScheduleMakeup() {
  const mutate = useMutation(api.coach.makeups.schedule);
  const [isPending, setIsPending] = useState(false);
  return {
    isPending,
    mutateAsync: async (args: { makeupId: string; makeupSessionId: string }) => {
      setIsPending(true);
      try {
        return await mutate({
          makeupId: args.makeupId as Id<"makeupSessions">,
          makeupSessionId: args.makeupSessionId as Id<"trainingSessions">,
        });
      } finally {
        setIsPending(false);
      }
    },
  };
}

export function useUpdateMakeupStatus() {
  const mutate = useMutation(api.coach.makeups.updateStatus);
  const [isPending, setIsPending] = useState(false);
  return {
    isPending,
    mutateAsync: async (args: {
      makeupId: string;
      status: "completed" | "waived";
      notes?: string;
    }) => {
      setIsPending(true);
      try {
        return await mutate({
          makeupId: args.makeupId as Id<"makeupSessions">,
          status: args.status,
          notes: args.notes,
        });
      } finally {
        setIsPending(false);
      }
    },
  };
}

// ════════════════════════════════════════════════════════════
// DIRECTOR HOOKS
// ════════════════════════════════════════════════════════════

export function useDirectorMakeups() {
  const data = useQuery(api.director.makeups.listOutstanding);
  return { data, isLoading: data === undefined };
}

export function useDirectorCompletedMakeups() {
  const data = useQuery(api.director.makeups.listCompleted);
  return { data, isLoading: data === undefined };
}

export function useDirectorScheduleMakeup() {
  const mutate = useMutation(api.director.makeups.schedule);
  const [isPending, setIsPending] = useState(false);
  return {
    isPending,
    mutateAsync: async (args: { makeupId: string; makeupSessionId: string }) => {
      setIsPending(true);
      try {
        return await mutate({
          makeupId: args.makeupId as Id<"makeupSessions">,
          makeupSessionId: args.makeupSessionId as Id<"trainingSessions">,
        });
      } finally {
        setIsPending(false);
      }
    },
  };
}

export function useDirectorUpdateMakeupStatus() {
  const mutate = useMutation(api.director.makeups.updateStatus);
  const [isPending, setIsPending] = useState(false);
  return {
    isPending,
    mutateAsync: async (args: {
      makeupId: string;
      status: "completed" | "waived";
      notes?: string;
    }) => {
      setIsPending(true);
      try {
        return await mutate({
          makeupId: args.makeupId as Id<"makeupSessions">,
          status: args.status,
          notes: args.notes,
        });
      } finally {
        setIsPending(false);
      }
    },
  };
}

export function useDirectorSessionsForDate(date: string) {
  const data = useQuery(api.director.sessions.listByDateRange, {
    startDate: date,
    endDate: date,
  });
  return { data, isLoading: data === undefined };
}

// ════════════════════════════════════════════════════════════
// PARENT HOOKS
// ════════════════════════════════════════════════════════════

export function useChildMakeups(childId: string | undefined) {
  const data = useQuery(
    api.parent.children.getMakeups,
    childId ? { childId: childId as Id<"players"> } : 'skip'
  );
  return { data, isLoading: data === undefined };
}
